import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region types
import { SettingsFormTypes } from "models/formTypes";
import {
  AutoCompleteExtraProps,
  CustomFormFieldsProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
  FormItemDefinitionType,
  GenericFormSection,
  TableListFieldProps,
  ToggleFieldExtraProps,
} from "models/propTypes/genericFormProps";
import { FormFieldConstants, IconDefinitionConstants, SettingsConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik/dist/types";
import { FormEnums, StyleEnums } from "models/enums";
import { Chip, Grid } from "@mui/material";
import { DimensionSettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
//#endregion
//#region components
import DimensionSettingsForm from "./dimensionSettingsForm";
import DimensionTypeInfoFormField from "./fields/dimensionTypeInfoFormField";
import { AddNewDimensionFormContainer, Skeletons, useMountedState, useUpdateStoreDimensions } from "components/common";
import { useSettingsService } from "serviceHooks";
import { useDimensionService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const DimensionSettingsFormContainer: ForwardRefRenderFunction<FormHandles, DimensionSettingsFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const dimensionService = useDimensionService();
  const [dimensionSettings, setDimensionSettings] = React.useState<WintSuperkollAPIModel.DimensionSetting>();
  const [dimensionGroups, setDimensionGroups] = React.useState<SettingsFormTypes.DimensionGroup[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.DimensionSettings>();
  const { getMounted } = useMountedState();

  const getValidations = () => {
    const val = {} as any;

    dimensionSettings?.dimensionSettingItems?.forEach((item) => {
      val[`${SettingsConstants.StandardDimension}${item.dimensionTypeName}`] = Yup.array().of(
        Yup.object<SettingsFormTypes.StandardDimension>({
          name: Yup.string(),
          dimensionId: Yup.string(),
          dimensionName: Yup.string(),
          isDemanded: Yup.boolean(),
          category: Yup.number(),
        })
      );
      val[`${SettingsConstants.DimensionList}${item.dimensionTypeName}`] = Yup.array()
        .of(
          Yup.object<SettingsFormTypes.BaseDimension>({
            name: Yup.string().required(TranslationKeys.Validations.Required),
            id: Yup.string(),
            shortName: Yup.string().required(TranslationKeys.Validations.Required),
            isActive: Yup.boolean(),
            canRemove: Yup.boolean(),
          })
        )
        .uniqueProperties(
          (item: any) => JSON.stringify({ shortName: item.shortName }) as any,
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.DimensionDuplicates)
        );
    });
    return val;
  };

  const validationSchema = Yup.object<SettingsFormTypes.DimensionSettings>({
    bookDefaultArticleDimensions: Yup.boolean(),
    ...getValidations(),
  });

  const getStandardDimensionByTypeId = (
    dimensionSettingItems?: WintSuperkollAPIModel.DimensionSettingItem[],
    typeId?: string
  ): SettingsFormTypes.StandardDimension[] => {
    const dim = dimensionSettingItems?.find((item) => item.dimensionTypeId === typeId);
    return [
      {
        name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.CustomerInvoices),
        dimensionId: dim?.defaultCustomerInvoiceDimensionId ?? "",
        dimensionName: dim?.defaultCustomerInvoiceDimensionName ?? "",
        isDemanded: dim?.isCustomerInvoiceDimensionRequired ?? false,
        category: SettingsFormTypes.StandardDimensionCategory.Invoice,
      },
      {
        name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.Receipts),
        dimensionId: dim?.defaultReceiptDimensionId ?? "",
        dimensionName: dim?.defaultReceiptDimensionName ?? "",
        isDemanded: dim?.isReceiptDimensionRequired ?? false,
        category: SettingsFormTypes.StandardDimensionCategory.Receipt,
      },
      {
        name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.IncomingInvoices),
        dimensionId: dim?.defaultSupplierInvoiceDimensionId ?? "",
        dimensionName: dim?.defaultSupplierInvoiceDimensionName ?? "",
        isDemanded: dim?.isSupplierInvoiceDimensionRequired ?? false,
        category: SettingsFormTypes.StandardDimensionCategory.IncomingInvoice,
      },
    ];
  };

  const getDimensionsListByTypeId = (
    dimensionSettingItems?: WintSuperkollAPIModel.DimensionSettingItem[],
    typeId?: string
  ): SettingsFormTypes.BaseDimension[] => {
    return (
      dimensionSettingItems
        ?.find((item) => item.dimensionTypeId === typeId)
        ?.dimensionList?.map((dim) => ({ ...dim, key: dim.id })) ?? []
    );
  };

  const getDefaultValues = (dimensionSettings: WintSuperkollAPIModel.DimensionSetting) => {
    const def: SettingsFormTypes.DimensionSettings = {
      bookDefaultArticleDimensions: dimensionSettings?.bookDefaultArticleDimensions ?? false,
    };

    dimensionSettings?.dimensionSettingItems?.forEach((item) => {
      def[`${SettingsConstants.StandardDimension}${item.dimensionTypeName}`] = getStandardDimensionByTypeId(
        dimensionSettings?.dimensionSettingItems,
        item.dimensionTypeId
      );
      def[`${SettingsConstants.DimensionList}${item.dimensionTypeName}`] = getDimensionsListByTypeId(
        dimensionSettings?.dimensionSettingItems,
        item.dimensionTypeId
      );
    });
    return def;
  };

  const onAddOrEditDimensionListItem = (dimensionTypeId: string, values?: WintSuperkollAPIModel.DimensionItem) => {
    return <AddNewDimensionFormContainer dimensionTypeId={dimensionTypeId} />;
  };

  const onDimensionListItemChange = (
    name: string,
    updatedValue: SettingsFormTypes.BaseDimension[],
    formikProps: FormikProps<any>
  ) => {
    const updatedData = dimensionGroups.map((d) =>
      d.fieldName === name ? { ...d, dimensionList: updatedValue.filter((v) => v.id) } : d
    );

    updateStandardDimensions(name, updatedValue, formikProps);
    setDimensionGroups(updatedData);
  };

  const updateStandardDimensions = (
    name: string,
    updatedValue: SettingsFormTypes.BaseDimension[],
    formikProps: FormikProps<any>
  ) => {
    const { values, setFieldValue } = formikProps;
    const dimensionName = dimensionGroups.find((d) => d.fieldName === name);
    const defaultDimensions = values[`${SettingsConstants.StandardDimension}${dimensionName?.dimensionTypeName ?? ""}`];

    if (defaultDimensions?.length) {
      const defaultDimensionsUpdated = [...defaultDimensions];
      let defaultsUpdated = false;

      defaultDimensionsUpdated.forEach((defaultSetting: SettingsFormTypes.StandardDimension) => {
        const isActive = updatedValue.find((val) => val.id === defaultSetting.dimensionId)?.isActive ?? false;
        if (!isActive) {
          defaultSetting.dimensionId = "";
          defaultSetting.dimensionName = "";

          defaultsUpdated = true;
        }
      });

      if (defaultsUpdated) {
        setFieldValue(`${SettingsConstants.StandardDimension}${name}`, defaultDimensionsUpdated);
      }
    }
  };

  const onDimensionListItemRemove = async (name: string, rowIndex: number, row: any, formikProps: FormikProps<any>) => {
    const { values, setFieldValue } = formikProps;
    const list: SettingsFormTypes.BaseDimension[] = values[`${name}`];
    const updatedList = list.filter((i) => i.id !== row.id);
    await dimensionService.deleteDimensionItem(row.id).then(() => setFieldValue(name, updatedList));
  };

  const formGroups = React.useMemo(() => {
    const generalGroup: GenericFormSection<SettingsFormTypes.DimensionSettings> = {
      label: TranslationKeys.Common.Generally,
      type: FormEnums.FormSectionType.FIELDGROUPSECTION,
      icon: IconDefinitionConstants.WInfo,
      className: cssExports.tableListRotatedExpansions,
      innerFields: {
        bookDefaultArticleDimensions: {
          label: TranslationKeys.Settings.UseArticleTags,
          descriptionText: TranslationKeys.Settings.UseArticleTagsDescription,
          type: FormEnums.FormFieldType.TOGGLE,
        },
      },
    };

    const dimensionSettingItemsGroups =
      dimensionSettings?.dimensionSettingItems?.map((item) => {
        const group = {
          label: item.dimensionTypeName,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WHashtag,
          className: cssExports.tableListRotatedExpansions,
          innerFields: {
            [`${SettingsConstants.ContactToActivate}${item.dimensionTypeName}`]: {
              label: "",
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <DimensionTypeInfoFormField dimensionType={item} />,
              extraProps: { formGridLayout: { message: false } } as CustomFormFieldsProps,
            },
            [`${SettingsConstants.StandardDimension}${item.dimensionTypeName}`]: {
              label: TranslationKeys.Settings.Standard,
              descriptionText: TranslationKeys.Settings.StandardDescription,
              type: FormEnums.FormFieldType.EDITABLETABLE,
              innerFields: {
                name: {
                  label: "",
                  type: FormEnums.FormFieldType.TEXT,
                  readonly: true,
                  widthColumnCount: 3,
                },
                dimensionName: {
                  label: "",
                  type: FormEnums.FormFieldType.AUTOCOMPLETE,
                  extraProps: {
                    referenceFieldName: FormFieldConstants.Settings.StandardDimension.DimensionId,
                    items: dimensionGroups
                      .find((t) => t.dimensionTypeId === item.dimensionTypeId)
                      ?.dimensionList.filter((dim) => dim.isActive)
                      .map((dim) => ({
                        id: dim.id,
                        value: dim.name,
                      }))
                      .sort(function (a, b) {
                        if (a.value !== undefined && b.value !== undefined) {
                          if (a.value < b.value) {
                            return -1;
                          }
                          if (a.value > b.value) {
                            return 1;
                          }
                        }
                        return 0;
                      }),
                  } as AutoCompleteExtraProps<SettingsFormTypes.StandardDimension>,
                },
                isDemanded: {
                  label: "",
                  type: FormEnums.FormFieldType.TOGGLE,
                  rightAlign: true,
                  extraProps: {
                    customLabels: {
                      active: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Compulsory),
                      inactive: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Compulsory),
                    },
                  } as ToggleFieldExtraProps,
                  classes: { formControl: clsx(cssExports.formFieldRightAlign, cssExports.floatRight) },
                },
              } as FormFieldDefinitionType<SettingsFormTypes.StandardDimension>,
              extraProps: {
                moreActions: () => [],
                isCompactTable: true,
                idFieldKey: FormFieldConstants.Settings.StandardDimension.Name,
              } as EditableTableFieldProps<SettingsFormTypes.StandardDimension>,
            },
            [`${SettingsConstants.DimensionList}${item.dimensionTypeName}`]: {
              label: TranslationKeys.Common.Tags,
              hidden: item.isSystemDimension,
              descriptionText: TranslationKeys.Settings.TagsDescription,
              type: FormEnums.FormFieldType.TABLELIST,
              onChangeHandler: onDimensionListItemChange,
              extraProps: {
                resultTableProperties: {
                  name: {
                    description: TranslationKeys.Settings.Name,
                    formatter: (value: SettingsFormTypes.BaseDimension) => (
                      <Grid item className={cssExports.tableListTagSettingsMainText}>
                        {value.name}
                      </Grid>
                    ),
                  },
                  shortName: {
                    description: TranslationKeys.Settings.ShortName,
                    formatter: (value: SettingsFormTypes.BaseDimension) => (
                      <Grid item className={cssExports.tableListTagSettingsRowText}>
                        {value.shortName}
                      </Grid>
                    ),
                  },
                  isActive: {
                    description: "",
                    formatter: (value: SettingsFormTypes.BaseDimension) => (
                      <Chip
                        label={LocalizationHelperInstance.getFormattedMessage(
                          value.isActive ? TranslationKeys.Common.Active : TranslationKeys.Common.InActive
                        )}
                        className={value.isActive ? cssExports.greenTag : cssExports.redTag}
                      />
                    ),
                    width: "100px",
                  },
                },
                activeKey: "isActive",
                searchKeys: ["name", "shortName"],
                searchPlaceholder: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.SearchTags),
                inactiveGroupTitle: TranslationKeys.Common.InActive2,
                moreActions: (row: SettingsFormTypes.BaseDimension) =>
                  !row.id || row.canRemove
                    ? [
                        {
                          description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
                          icon: IconDefinitionConstants.WTrashAlt,
                          highlightType: StyleEnums.DisplayType.Danger,
                          onClickHandler: (rowIndex: number, row: any, formikProps: FormikProps<any>) =>
                            onDimensionListItemRemove(
                              `${SettingsConstants.DimensionList}${item.dimensionTypeName}`,
                              rowIndex,
                              row,
                              formikProps
                            ),
                        },
                      ]
                    : [],
                entityText: `${TranslationKeys.Common.Tag}`,
                addItemForm: (row: WintSuperkollAPIModel.DimensionItem) =>
                  onAddOrEditDimensionListItem(item.dimensionTypeId ?? "", row),
                orderByProperty: "name",
              } as TableListFieldProps<SettingsFormTypes.BaseDimension>,
            },
          },
        } as GenericFormSection<any>;
        return group;
      }) ?? [];

    const sections = [generalGroup].concat(dimensionSettingItemsGroups);

    return sections as FormItemDefinitionType<any>;
  }, [dimensionGroups]);

  const getDimensionTypeValues = (
    standardDimensionType: SettingsFormTypes.StandardDimensionCategory,
    standardDimensions: SettingsFormTypes.StandardDimension[]
  ) => {
    return standardDimensions.find((dim) => dim.category === standardDimensionType);
  };

  const { updateStoreDimensions } = useUpdateStoreDimensions();

  const handleSubmit = async (values: SettingsFormTypes.DimensionSettings) => {
    const items = dimensionSettings?.dimensionSettingItems?.map((item) => {
      const standardDimensions = values[`${SettingsConstants.StandardDimension}${item.dimensionTypeName}`];
      const invoiceData = getDimensionTypeValues(
        SettingsFormTypes.StandardDimensionCategory.Invoice,
        standardDimensions
      );
      const receiptData = getDimensionTypeValues(
        SettingsFormTypes.StandardDimensionCategory.Receipt,
        standardDimensions
      );
      const incomingInvoiceData = getDimensionTypeValues(
        SettingsFormTypes.StandardDimensionCategory.IncomingInvoice,
        standardDimensions
      );
      const dimensionPut: WintSuperkollAPIModel.DimensionSettingItemPut = {
        dimensionTypeId: item.dimensionTypeId,
        defaultCustomerInvoiceDimensionId: invoiceData?.dimensionId,
        defaultReceiptDimensionId: receiptData?.dimensionId,
        defaultSupplierInvoiceDimensionId: incomingInvoiceData?.dimensionId,
        isCustomerInvoiceDimensionRequired: invoiceData?.isDemanded ?? false,
        isReceiptDimensionRequired: receiptData?.isDemanded ?? false,
        isSupplierInvoiceDimensionRequired: incomingInvoiceData?.isDemanded ?? false,
        dimensionList: values[`${SettingsConstants.DimensionList}${item.dimensionTypeName}`].map(
          (d: SettingsFormTypes.BaseDimension): WintSuperkollAPIModel.DimensionItem => {
            return {
              id: d.id,
              name: d.name,
              shortName: d.shortName,
              isActive: d.isActive,
              canRemove: d.canRemove,
            };
          }
        ),
      };

      return dimensionPut;
    });

    const settingsPut: WintSuperkollAPIModel.DimensionSettingPut = {
      bookDefaultArticleDimensions: values.bookDefaultArticleDimensions,
      dimensionSettingItems: items,
    };

    return settingsService.updateDimensionSettings(settingsPut).then((res) => {
      loadSettings();
      updateStoreDimensions();
    });
  };

  const loadSettings = () => {
    settingsService.getDimensionSettings().then((res) => {
      if (getMounted()) {
        setDefaultValues(getDefaultValues(res));
        setDimensionSettings(res);
        const list = res.dimensionSettingItems?.map((item): SettingsFormTypes.DimensionGroup => {
          return {
            fieldName: `${SettingsConstants.DimensionList}${item.dimensionTypeName}`,
            dimensionTypeId: item.dimensionTypeId ?? "",
            dimensionTypeName: item.dimensionTypeName ?? "",
            dimensionList: item.dimensionList as SettingsFormTypes.BaseDimension[],
          };
        });
        list && setDimensionGroups(list);
        setLoading(false);
      }
    });
  };

  React.useEffect(() => {
    loadSettings();
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <DimensionSettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
      formGroup={formGroups}
    />
  );
};

export default forwardRef(DimensionSettingsFormContainer);
